import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { PushAction } from "@/entities/messaging/types";
import {
  useLeadCandidateAcceptMutation,
  useLeadCandidateActionState,
  useLeadCandidateRejectMutation,
  useLeadCandidateUnacceptMutation,
  useLeadCandidateUnrejectMutation
} from "@/entities/messaging/leadCandidateHooks";

interface Props {
  action: PushAction;
}

const PREFILL_KEYS = ["firstName", "lastName", "companyName", "email"] as const;

/**
 * "Create now" / "Create manually" + "Not a lead" buttons on a Dreamer lead
 * suggestion notification. State (taken/reversible) derives from the candidate
 * row, so it stays consistent across recipients and reloads. Selecting the
 * alternative action reverses the taken one (sequential mutations).
 */
export default function DreamerLeadActions({ action }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const candidateId = action.candidateId ?? "";
  const isManual = action.type === "dreamer_lead_create_manual";
  const stateQuery = useLeadCandidateActionState(candidateId, !!candidateId);
  const acceptCandidate = useLeadCandidateAcceptMutation();
  const unacceptCandidate = useLeadCandidateUnacceptMutation();
  const rejectCandidate = useLeadCandidateRejectMutation();
  const unrejectCandidate = useLeadCandidateUnrejectMutation();
  // One in-flight gate for both buttons — an action can be a two-step reversal
  // (e.g. unaccept then reject), so individual isPending flags aren't enough.
  const [isWorking, setIsWorking] = useState(false);

  const candidateState = stateQuery.data?.candidates?.[0];
  if (!candidateState) {
    // Candidate not loaded yet or no longer available — no actionable buttons.
    return null;
  }

  const isCreated = !!candidateState.createdLeadId;
  const isRejected = candidateState.rejected;
  const pathParams = { candidateId };

  const handleCreate = async () => {
    setIsWorking(true);
    try {
      if (isRejected) {
        // Reversal: clear "Not a lead" first, then create.
        await unrejectCandidate.mutateAsync({ pathParams });
      }
      if (isManual) {
        const prefill = action.leadPrefill ?? {};
        const searchParams = new URLSearchParams();
        PREFILL_KEYS.forEach((key) => {
          if (prefill[key]) searchParams.set(key, prefill[key]!);
        });
        const query = searchParams.toString();
        navigate(`/leads?new${query ? `&${query}` : ""}`);
      } else {
        await acceptCandidate.mutateAsync({ pathParams });
      }
    } catch {
      toast.error(t("chat.dreamerLeadActions.createError"));
    } finally {
      setIsWorking(false);
      stateQuery.refetch();
    }
  };

  const handleNotALead = async () => {
    setIsWorking(true);
    try {
      if (isCreated) {
        // Reversal: remove the created lead first, then record the rejection.
        await unacceptCandidate.mutateAsync({ pathParams });
      }
      await rejectCandidate.mutateAsync({ pathParams });
    } catch {
      toast.error(t("chat.dreamerLeadActions.rejectError"));
    } finally {
      setIsWorking(false);
      stateQuery.refetch();
    }
  };

  return (
    <div className="px-5 pt-2 flex items-center gap-3">
      <Button
        variant="secondary"
        size="lg"
        disabled={isWorking || isCreated}
        onClick={handleCreate}
        data-test="dreamer-lead-create-button"
      >
        {isManual
          ? t("chat.dreamerLeadActions.createManually")
          : t("chat.dreamerLeadActions.createNow")}
      </Button>
      <Button
        variant="secondary"
        size="lg"
        disabled={isWorking || isRejected}
        onClick={handleNotALead}
        data-test="dreamer-lead-reject-button"
      >
        {t("chat.dreamerLeadActions.notALead")}
      </Button>
    </div>
  );
}
